// src/components/layout/SideBar.tsx
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { NavLink } from 'react-router-dom';
import './SideBar.scss';
import cn from 'classnames'; // Use classnames library
import { Home, TvMinimal, Building, ArrowLeftToLine, ArrowRight, ChevronDown, MessageSquareText, User, Users, Globe, Activity, MessageCircle, Edit, Monitor, Zap, FlaskConical, ClipboardList, ClipboardCheck, Package, BarChart2, Trophy, Star, Inbox, FileBarChart, UserPlus, Contact,} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import UpgradeModal from '../trial/UpgradeModal';
import { useAssignmentCount } from '../../contexts/AssignmentCountContext';
import { useCanViewJoinRequests } from '../join-requests/useCanViewJoinRequests';


interface SideBarProps {
    isCollapsed: boolean;
    onToggle: () => void; // Function to toggle the collapse state
    isMobileOpen: boolean;
    onCloseMobile: () => void;
}

interface NavItem {
    name: string;
    icon: React.ReactNode;
    path: string;
    roles: string[];
    marketplaceBlocked: boolean;
    // Optional DOM id used to anchor the interactive tutorial (see components/tutorial/interactiveSteps.ts).
    tourId?: string;
}

// Nav items are grouped into labeled sections so the sidebar stays legible as the
// product grows: new features slot into the right section rather than lengthening
// one flat list. Items marked marketplaceBlocked are hidden for marketplace users
// regardless of role. A section renders only if the current role can see at least
// one of its items; when just one section is visible (e.g. trainees) the header is omitted.
const navGroups: Array<{ section: string; items: NavItem[] }> = [
    {
        section: 'Practice',
        items: [
            { name: 'Home', icon: <Home />, path: '/', roles: ['super_admin', 'admin', 'trainer', 'trainee'], marketplaceBlocked: false, tourId: 'tour-nav-home' },
            { name: 'Role Play', icon: <TvMinimal />, path: '/roleplay', roles: ['super_admin', 'admin', 'trainer', 'trainee'], marketplaceBlocked: false, tourId: 'tour-nav-roleplay' },
            { name: 'My Assignments', icon: <ClipboardList />, path: '/my-assignments', roles: ['admin', 'trainer', 'trainee'], marketplaceBlocked: false, tourId: 'tour-nav-my-assignments' },
            { name: 'Feedback', icon: <MessageSquareText />, path: '/sessions', roles: ['super_admin', 'admin', 'trainer', 'trainee'], marketplaceBlocked: false, tourId: 'tour-nav-feedback' },
            { name: 'My Progress', icon: <BarChart2 />, path: '/my-progress', roles: ['super_admin', 'admin', 'trainer', 'trainee'], marketplaceBlocked: false, tourId: 'tour-nav-my-progress' },
            { name: 'Competitions', icon: <Trophy />, path: '/competitions', roles: ['super_admin', 'admin', 'trainer', 'trainee'], marketplaceBlocked: false, tourId: 'tour-nav-competitions' },
        ],
    },
    {
        section: 'Manage Training',
        items: [
            { name: 'Assignments', icon: <ClipboardCheck />, path: '/assignments', roles: ['super_admin', 'admin', 'trainer'], marketplaceBlocked: false, tourId: 'tour-nav-assignments' },
            { name: 'Reviews', icon: <Star />, path: '/reviews', roles: ['super_admin', 'admin', 'trainer'], marketplaceBlocked: false, tourId: 'tour-nav-reviews' },
            { name: 'My Reviews', icon: <Inbox />, path: '/reviewer-dashboard', roles: ['super_admin', 'admin', 'trainer'], marketplaceBlocked: false, tourId: 'tour-nav-reviewer-dashboard' },
            { name: 'Bundles', icon: <Package />, path: '/bundles', roles: ['super_admin', 'admin'], marketplaceBlocked: false, tourId: 'tour-nav-bundles' },
            { name: 'Team Progress', icon: <Users />, path: '/team-progress', roles: ['admin', 'trainer'], marketplaceBlocked: false, tourId: 'tour-nav-team-progress' },
            { name: 'Users', icon: <User />, path: '/users', roles: ['super_admin', 'admin', 'trainer'], marketplaceBlocked: false, tourId: 'tour-nav-users' },
            // Sits next to Users because it is the same job: deciding who is in the org.
            // Admin always sees it; a Trainer only with approval rights (Billing Owner or the
            // per-account switch) — isVisible() hides it otherwise, since the server 403s the
            // list for a Trainer without them (see useCanViewJoinRequests). Not super admin:
            // they have no org, so the page is only a "go to Organizations" dead end — their
            // queues live on each org's detail page. "View as" admin/trainer still shows it.
            { name: 'Join Requests', icon: <UserPlus />, path: '/join-requests', roles: ['admin', 'trainer'], marketplaceBlocked: false, tourId: 'tour-nav-join-requests' },
            { name: 'Dashboard', icon: <Activity />, path: '/dashboard', roles: ['super_admin', 'admin', 'trainer'], marketplaceBlocked: false, tourId: 'tour-nav-dashboard' },
        ],
    },
    {
        section: 'Setup & Admin',
        items: [
            { name: 'Characters', icon: <Contact />, path: '/characters', roles: ['super_admin', 'admin'], marketplaceBlocked: true, tourId: 'tour-nav-characters' },
            { name: 'Prompt Studio', icon: <Edit />, path: '/prompt-studio', roles: ['super_admin', 'admin'], marketplaceBlocked: true, tourId: 'tour-nav-prompt-studio' },
            { name: 'Scene Editor', icon: <Monitor />, path: '/scene-editor', roles: ['super_admin', 'admin'], marketplaceBlocked: true, tourId: 'tour-nav-scene-editor' },
            { name: 'Organizations', icon: <Building />, path: '/organizations', roles: ['super_admin'], marketplaceBlocked: true, tourId: 'tour-nav-organizations' },
            { name: 'Platform Overview', icon: <Globe />, path: '/platform-dashboard', roles: ['super_admin'], marketplaceBlocked: true, tourId: 'tour-nav-platform-dashboard' },
            { name: 'Reports', icon: <FileBarChart />, path: '/reports', roles: ['super_admin'], marketplaceBlocked: true, tourId: 'tour-nav-reports' },
            { name: 'App Feedback', icon: <MessageCircle />, path: '/feedback', roles: ['super_admin', 'admin'], marketplaceBlocked: true, tourId: 'tour-nav-app-feedback' },
        ],
    },
];

// Trial users only get the core loop: Home, Role Play, Feedback. Role Play moved
// from '/' to '/roleplay' when Home took over the root route, keep both listed.
const TRIAL_PATHS = ['/', '/roleplay', '/sessions'];

const COLLAPSED_SECTIONS_KEY = 'rc_sidebar_collapsed_sections';

function loadCollapsedSections(): Record<string, boolean> {
    try { return JSON.parse(localStorage.getItem(COLLAPSED_SECTIONS_KEY) || '{}'); }
    catch { return {}; }
}

const SideBar: React.FC<SideBarProps> = ({ isCollapsed, onToggle, isMobileOpen, onCloseMobile }) => {
    const { isMarketplaceUser, isTrialUser, isTrialExhausted, trialSessionsUsed, trialSessionLimit, userRole: authRole, currentUser } = useAuth();
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    // Org already has a Billing Owner other than this person — self-serve upgrade isn't
    // a seated/invited teammate's call to make, so the CTA that would have them pay again
    // is hidden for them. Read directly off the org (populated for every role via
    // /user/{uid}), not off the join-request record — a user who joined the org by
    // self-signup rather than the join-request flow (e.g. the org's very first member)
    // has no JoinRequest row at all, so that signal always read "no Billing Owner" for
    // them regardless of the org's actual state.
    // The Billing Owner themself must still see this once THEIR OWN paid access has
    // expired — `is_org_billing_owner` excludes them from the suppression, otherwise an
    // expired owner has no renew path anywhere in the app (same fix as GeminiDirectStreamPage).
    // `is_org_billing_owner` is organizations.billing_owner_id === me, which is NULL for every
    // org that subscribed before that column existed; `is_billing_owner` (their own
    // stripe_customer_id) stays true for those legacy owners, so accept either.
    // `invited_by` covers the same legacy orgs from the teammate's side: has_billing_owner
    // reads false there, but someone the owner invited or seated still has nothing of their
    // own to upgrade — same definition as GeminiDirectStreamPage.
    const isBillingOwner = Boolean(currentUser?.is_org_billing_owner || currentUser?.is_billing_owner);
    const hasOrgBillingOwner =
        Boolean(currentUser?.organization?.has_billing_owner || currentUser?.invited_by) && !isBillingOwner;
    // A paid billing owner whose access_end_date has passed is not a "trial" user, so
    // isTrialUser alone would never show them the Upgrade button — isTrialExhausted also
    // covers a paid subscription's access_end_date, so include it here too.
    const showUpgradeButton = isTrialUser || (isTrialExhausted && isBillingOwner);
    // A plain Trainer with no approval rights gets a 403 from the list endpoint (see
    // list_join_requests) — no point showing a nav link that dead-ends in a redirect.
    const canViewJoinRequests = useCanViewJoinRequests();
    const { count: assignmentCount } = useAssignmentCount();
    const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(loadCollapsedSections);

    // Close the mobile drawer on Escape, matching driver.js's own overlay-dismiss convention.
    // Skipped while the upgrade modal (portaled above the drawer, see UpgradeModal.scss) is
    // open, so Escape dismisses that topmost overlay instead of silently closing the drawer
    // behind it.
    useEffect(() => {
        if (!isMobileOpen || showUpgradeModal) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onCloseMobile();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isMobileOpen, showUpgradeModal, onCloseMobile]);

    // The upgrade modal itself has no Escape handler — closing it here keeps
    // Escape acting on whichever overlay is actually on top.
    useEffect(() => {
        if (!showUpgradeModal) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setShowUpgradeModal(false);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showUpgradeModal]);

    // Use the reactive role from AuthContext so the sidebar always reflects the
    // same role as the rest of the app. localStorage.getItem('role') is only
    // updated on a fresh API fetch and can lag behind when the 30-min authCache
    // fast-path is used, causing the sidebar and profile badge to disagree.
    const userRole = authRole || localStorage.getItem('role') || 'trainee';

    const isVisible = (item: NavItem) => {
        if (!item.roles.includes(userRole)) return false;
        // Admin always sees Join Requests — only a Trainer's access
        // depends on approval rights, so the hook is only consulted for that role.
        if (item.path === '/join-requests' && userRole === 'trainer' && !canViewJoinRequests) return false;
        if (isMarketplaceUser && item.marketplaceBlocked && userRole !== 'super_admin') return false;
        if (isTrialUser && !TRIAL_PATHS.includes(item.path)) return false;
        return true;
    };

    // Only sections with at least one visible item are shown.
    const visibleGroups = navGroups
        .map(g => ({ section: g.section, items: g.items.filter(isVisible) }))
        .filter(g => g.items.length > 0);

    // With a single visible section (e.g. trainees) the header adds no value, so
    // render a flat list instead.
    const showSectionHeaders = visibleGroups.length > 1 && !isCollapsed;

    const toggleSection = (section: string) => {
        setCollapsedSections(prev => {
            const next = { ...prev, [section]: !prev[section] };
            try { localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify(next)); } catch { /* storage blocked */ }
            return next;
        });
    };

    const renderItem = (item: NavItem) => (
        <li key={item.name} title={isCollapsed ? item.name : undefined}>
            <NavLink
                to={item.path || '#'}
                id={item.tourId}
                className={({ isActive }) => cn('nav-item', { 'active': isActive })}
                end={item.path === '/'}
                // .nav-text is display:none while collapsed, which drops it from the
                // accessibility tree — name the icon-only link explicitly then.
                aria-label={isCollapsed
                    ? (item.path === '/my-assignments' && assignmentCount > 0 ? `${item.name} (${assignmentCount})` : item.name)
                    : undefined}
                onClick={() => { if (isMobileOpen) onCloseMobile(); }}
            >
                <div className="nav-icon" aria-hidden="true"> {item.icon}</div>
                <span className="nav-text">{item.name}</span>
                {item.path === '/my-assignments' && assignmentCount > 0 && (
                    <span className="nav-badge">{assignmentCount}</span>
                )}
            </NavLink>
        </li>
    );

    return (
        <>
        {/* Mouse/touch shortcut only — Escape closes the drawer for keyboard users. */}
        {isMobileOpen && <div className="sidebar-backdrop" aria-hidden="true" onClick={onCloseMobile} />}
        <aside className={cn('sidebar', { 'collapsed': isCollapsed, 'open': isMobileOpen })}>
            {/* Collapse toggle */}
            <button
                className="button-ghost collapse-button-top" // New class name
                type="button"
                onClick={onToggle}
                aria-expanded={!isCollapsed}
                title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
                aria-label={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
            >
                {isCollapsed ? <ArrowRight aria-hidden="true" /> : <ArrowLeftToLine aria-hidden="true" />}
            </button>

            <div className="sidebar-header">
                <div className="logo-container">
                    {!isCollapsed && (
                        <img src="/assets/branding/rc-logo-light.png" alt="Reality Coach" className="logo-full" />
                    )}
                    <img src="/assets/branding/rc-logo-icon.png" alt="RC" className="logo-mini" />
                </div>
            </div>

            <nav className="sidebar-nav" aria-label="Main">
                {visibleGroups.map((group, gi) => {
                    const isSectionCollapsed = !!collapsedSections[group.section];
                    const sectionListId = `nav-section-${group.section.replace(/\s+/g, '-').toLowerCase()}`;
                    return (
                        <div key={group.section} className="nav-section">
                            {showSectionHeaders ? (
                                <button
                                    type="button"
                                    className="nav-section-header"
                                    onClick={() => toggleSection(group.section)}
                                    aria-expanded={!isSectionCollapsed}
                                    aria-controls={sectionListId}
                                >
                                    <span>{group.section}</span>
                                    {/* Points down when closed (click to drop the items open),
                                        flips up when open — the standard accordion affordance. */}
                                    <ChevronDown
                                        size={15}
                                        className="nav-section-chevron"
                                        style={{ transform: isSectionCollapsed ? 'none' : 'rotate(180deg)', transition: 'transform 0.18s ease' }}
                                        aria-hidden="true"
                                    />
                                </button>
                            ) : (
                                // In icon-only (collapsed) mode, separate groups with a divider
                                // instead of a text header. No divider before the first group.
                                isCollapsed && gi > 0 && <div className="nav-section-divider" aria-hidden="true" />
                            )}
                            {/* Hidden rather than unmounted: the header's aria-controls has to
                                resolve to a real element precisely when the section is
                                collapsed, which is the only state where the relationship
                                matters. */}
                            <ul id={sectionListId} hidden={showSectionHeaders && isSectionCollapsed}>
                                {group.items.map(renderItem)}
                            </ul>
                        </div>
                    );
                })}
            </nav>

            {showUpgradeButton && !hasOrgBillingOwner && (
                <button
                    type="button"
                    className={cn('upgrade-button', { 'upgrade-button--collapsed': isCollapsed })}
                    onClick={() => setShowUpgradeModal(true)}
                    title="Upgrade your plan"
                >
                    <Zap className="upgrade-button__icon" />
                    {!isCollapsed && <span>Upgrade</span>}
                </button>
            )}
            {isTrialUser && (
                <div
                    className={cn('trial-badge', { 'trial-badge--collapsed': isCollapsed })}
                    title={`Trial: ${trialSessionsUsed}/${trialSessionLimit} sessions used`}
                >
                    <FlaskConical className="trial-badge__icon" />
                    {!isCollapsed && <span>Trial · {trialSessionsUsed}/{trialSessionLimit} sessions</span>}
                </div>
            )}

        </aside>
        {showUpgradeModal && ReactDOM.createPortal(
            <UpgradeModal onClose={() => setShowUpgradeModal(false)} />,
            document.body
        )}
        </>
    );
};

export default SideBar;
